import {
  faArrowRight,
  faCheck,
  faCopy,
  faEllipsisVertical,
  faFingerprint,
  faLaptopCode,
  faPen,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Button,
  Drawer,
  Dropdown,
  Grid,
  Icon,
  Inline,
  Input,
  Label,
  Message,
  Section,
  Tooltip,
} from "@intility/bifrost-react";
import { useState } from "react";
import { useClient, useUpdateClientSpiffe } from "../api/clients";
import type { ClientListItem } from "../api/types";
import { ClientLocalDevDrawer } from "./ClientLocalDevDrawer";
import { clientTypeState } from "./ClientTypeBadge";
import { useCanEditClient, useCanManageClient } from "./useCanEditClient";

// The SPIFFE ID without its trust domain: `<cluster>/ns/<ns>/sa/<sa>`.
function spiffePathOf(spiffeId: string | null): string | undefined {
  return spiffeId?.replace(/^spiffe:\/\/[^/]+\//, "");
}

// Splits that path back into the three values the drawer edits.
function parseSpiffePath(path: string | undefined) {
  const parts = path?.split("/");
  return {
    cluster: parts?.[0] && parts[0] !== "intility-id" ? parts[0] : "",
    namespace: parts?.[1] === "ns" ? (parts[2] ?? "") : "",
    serviceAccount: parts?.[3] === "sa" ? (parts[4] ?? "") : "",
  };
}

// A value in a box with a copy button, for IDs the user pastes elsewhere.
export function CodeRow({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div className="client-api-node">
      <code
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: "monospace",
          wordBreak: "break-all",
        }}
      >
        {value}
      </code>
      <Tooltip content={copied ? "Copied" : "Copy"}>
        <Button
          variant="flat"
          small
          className="client-icon-button"
          aria-label="Copy"
          onClick={copy}
        >
          <Icon icon={copied ? faCheck : faCopy} />
        </Button>
      </Tooltip>
    </div>
  );
}

// The workload identity of a client that authenticates with SPIFFE: the SPIFFE
// ID it gets tokens for and, for an editor, where it can be changed. Clients
// with a client secret have no workload identity, so they get no section.
export function ClientWorkloadIdentity({ client }: { client: ClientListItem }) {
  const { data: detail } = useClient(client.id);
  const canEdit = useCanEditClient(client);
  const canManage = useCanManageClient(client);
  const [editing, setEditing] = useState(false);
  const [localDevOpen, setLocalDevOpen] = useState(false);

  if (detail?.authMethod !== "spiffe") return null;

  const spiffeId = detail.spiffeId;
  const path = spiffePathOf(spiffeId);
  // A workload on another cluster is matched by namespace and service account;
  // one on this cluster also needs the pod label.
  const isConsumerCluster = path ? !path.startsWith("intility-id/") : false;

  return (
    <Section
      border
      radius
      background="base-3"
      className="client-object-workload"
    >
      <Section.Header>
        <Inline align="center">
          <Icon icon={faFingerprint} />
          <Inline.Stretch>Workload identity</Inline.Stretch>
          {/* Both ways of getting a credential: set the workload identity, or
              run locally with the companion's secret. */}
          <Dropdown
            placement="bottom-end"
            disabled={!canEdit && !canManage}
            content={
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (canEdit) setEditing(true);
                  }}
                >
                  <Dropdown.Item
                    icon={faPen}
                    state={canEdit ? undefined : "inactive"}
                  >
                    Set workload identity
                  </Dropdown.Item>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (canManage) setLocalDevOpen(true);
                  }}
                >
                  <Dropdown.Item
                    icon={faLaptopCode}
                    state={canManage ? undefined : "inactive"}
                  >
                    Run locally
                  </Dropdown.Item>
                </button>
              </>
            }
          >
            <Button
              variant="flat"
              small
              className="client-icon-button"
              aria-label="More"
              title="More"
              disabled={!canEdit && !canManage}
            >
              <Icon icon={faEllipsisVertical} />
            </Button>
          </Dropdown>
        </Inline>
      </Section.Header>
      <Section.Content className="client-object-workload-body">
        {!spiffeId ? (
          <>
            {/* What to set is explained in the edit drawer. */}
            <Message
              state={canEdit ? clientTypeState(client.appType) : "neutral"}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                <strong>Workload identity not set</strong>
                <Button
                  variant="flat"
                  small
                  className={canEdit ? "client-type-button" : undefined}
                  data-type={clientTypeState(client.appType)}
                  style={{ flexShrink: 0 }}
                  disabled={!canEdit}
                  onClick={() => setEditing(true)}
                >
                  Set it
                  <Icon icon={faArrowRight} marginLeft />
                </Button>
              </div>
            </Message>
            {/* A laptop has no SPIFFE identity, so while the workload identity
                is missing the client can run locally with a secret instead.
                Only owners can set that up. */}
            <Message state="neutral">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                <span>
                  You can run it locally while you wait for the workload
                  identity.
                </span>
                <Button
                  variant="flat"
                  small
                  style={{ flexShrink: 0 }}
                  disabled={!canManage}
                  onClick={() => setLocalDevOpen(true)}
                >
                  Run locally
                  <Icon icon={faArrowRight} marginLeft />
                </Button>
              </div>
            </Message>
          </>
        ) : (
          <>
            <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
              {isConsumerCluster
                ? "This client authenticates with a SPIFFE SVID from a consumer cluster. The remote SPIRE agent selects workloads by namespace and service account, so no pod label is needed."
                : "This client authenticates with a SPIFFE SVID, with no secret. Add the pod label below to your workload's pod template, mount the SPIFFE CSI socket, and grant spire-workload-scc to its service account."}
            </p>
            {/* The SPIFFE ID is also the credential subject, so one row says
                both. */}
            <div>
              <Label>SPIFFE ID / Credential subject</Label>
              <CodeRow value={spiffeId} />
            </div>
            {!isConsumerCluster && (
              <div>
                <Label>Pod label</Label>
                <CodeRow value={`spiffe.io/spiffe-id-path: ${path}`} />
              </div>
            )}
          </>
        )}
      </Section.Content>
      <WorkloadIdentityDrawer
        client={client}
        spiffeId={spiffeId}
        open={editing}
        onClose={() => setEditing(false)}
      />
      {canManage && (
        <ClientLocalDevDrawer
          client={client}
          open={localDevOpen}
          onClose={() => setLocalDevOpen(false)}
        />
      )}
    </Section>
  );
}

// Edits where the workload runs: cluster, namespace and service account. The
// form starts over from the current values each time the drawer opens.
function WorkloadIdentityDrawer({
  client,
  spiffeId,
  open,
  onClose,
}: {
  client: ClientListItem;
  spiffeId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSession((s) => s + 1);
  }
  return (
    <DrawerBody
      key={session}
      client={client}
      spiffeId={spiffeId}
      open={open}
      onClose={onClose}
    />
  );
}

function DrawerBody({
  client,
  spiffeId,
  open,
  onClose,
}: {
  client: ClientListItem;
  spiffeId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateClientSpiffe(client.id);
  const initial = parseSpiffePath(spiffePathOf(spiffeId));
  const [cluster, setCluster] = useState(initial.cluster);
  const [namespace, setNamespace] = useState(initial.namespace);
  const [serviceAccount, setServiceAccount] = useState(initial.serviceAccount);

  const ns = namespace.trim();
  const sa = serviceAccount.trim();

  function save() {
    if (update.isPending) return;
    update.mutate(
      {
        spiffeNamespace: ns || undefined,
        spiffeServiceAccount: sa || undefined,
        spiffeCluster: cluster.trim() || undefined,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Drawer
      isOpen={open}
      onRequestClose={() => {
        if (!update.isPending) onClose();
      }}
      header="Edit workload identity"
      footer={
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            onClick={onClose}
            state={update.isPending ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={save}
            state={update.isPending ? "inactive" : undefined}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      }
    >
      <Grid gap={16} style={{ marginTop: 24 }}>
        {!spiffeId && (
          <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
            This client cannot get a token yet. Set the namespace and service
            account where the workload runs.
          </p>
        )}
        <Input
          label="Cluster"
          optional
          value={cluster}
          onChange={(e) => setCluster(e.target.value)}
          placeholder="Leave empty for in-cluster"
          feedback="Defaults to intility-id when empty. Set for consumer-cluster workloads."
        />
        <Input
          label="Namespace"
          optional
          value={namespace}
          onChange={(e) => setNamespace(e.target.value)}
          placeholder="e.g. my-service"
          feedback="Workload namespace on the cluster."
        />
        <Input
          label="Service account"
          optional
          value={serviceAccount}
          onChange={(e) => setServiceAccount(e.target.value)}
          placeholder="e.g. my-sa"
          feedback="Workload service account on the cluster."
        />
        {ns && sa && (
          <Message state="neutral">
            Credential subject:{" "}
            <code>
              spiffe://&lt;trust-domain&gt;/{cluster.trim() || "intility-id"}
              /ns/{ns}/sa/{sa}
            </code>
          </Message>
        )}
        {update.error && (
          <Message state="warning" header="Update failed">
            {update.error instanceof Error
              ? update.error.message
              : "An unexpected error occurred."}
          </Message>
        )}
      </Grid>
    </Drawer>
  );
}
